/**
 * Adapter for the Orders screens.
 *
 * The Orders feature landed as plain components (`OrdersRoute`, `OrderDetailRoute`) that
 * take the order id as a prop rather than as route files exporting `Route`. That is a
 * reasonable split: it keeps the router in one place. This file is the one place that
 * knows about it.
 *
 * Both navigations carry the search string through unchanged. Opening an order must not
 * throw away the queue, the range and the search that found it, and closing the drawer must
 * land back on the same list: that is what makes the drawer feel like it is over the page
 * rather than instead of it.
 */
import { createRoute, useNavigate, useParams } from '@tanstack/react-router';
import { OrdersRoute } from '@/routes/orders/index';
import { OrderDetailRoute } from '@/routes/orders/$orderId';
import { Route as shellRoute } from '@/routes/_shell';

function useOpenOrder() {
  const navigate = useNavigate();
  return (orderId: number | null) =>
    void navigate({
      to: orderId === null ? '/orders' : `/orders/${orderId}`,
      search: (prev: Record<string, unknown>) => prev,
    });
}

function OrdersScreen() {
  return <OrdersRoute onOrderIdChange={useOpenOrder()} />;
}

function OrderDetailScreen() {
  const params = useParams({ strict: false }) as { orderId?: string };
  const orderId = Number(params.orderId ?? 0);
  return <OrderDetailRoute orderId={orderId} onOrderIdChange={useOpenOrder()} />;
}

export const ordersRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/orders',
  component: OrdersScreen,
});

export const orderDetailRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/orders/$orderId',
  component: OrderDetailScreen,
});
